(ns ui.editor2.emoji-catalog
  "The emojis of the editor2 Emoji sidebar: the custom Storrito stickers
   first, then the emoji-mart Google set without flags. Every emoji is a
   map with its emoji-mart `:id` (the custom ones use their first short
   name, like emoji-mart did), a readable `:label` and the `:uri` of the
   image that is added to the design: a Noto SVG under
   /emojicons/noto-emoji or the PNG of a custom emoji. Emojis without a
   Noto SVG are left out, they could not be added."
  (:require ["emoji-mart" :as emojimart]
            ["emoji-mart/data/google.json" :as emoji-data]
            [clojure.string :as str]
            [storrito.instagram.story.editor.emojis :as custom-emojis])
  (:require-macros [ui.editor2.noto-emoji :as noto-emoji]))

(def noto-codes
  "The code points of the Noto SVGs the app serves, read at build time."
  (noto-emoji/available-codes))

(defn noto-code
  "The code points of an emoji as used in the Noto file names, e.g.
   \"2764-fe0f\" → \"2764\" and \"1f9cf-200d-2642-fe0f\" →
   \"1f9cf_200d_2642\"."
  [unified]
  (-> unified
      (str/replace "-fe0f" "")
      (str/replace "-" "_")))

(defn noto-uri
  "The path of the Noto SVG for the `unified` code points of an emoji,
   e.g. \"2764-fe0f\" → \"/emojicons/noto-emoji/emoji_u2764.svg\"."
  [unified]
  (str "/emojicons/noto-emoji/emoji_u" (noto-code unified) ".svg"))

(defn label
  "A readable name of the emoji `id`, e.g. \"heart eyes\"."
  [id]
  (str/replace id "_" " "))

(defn custom-emoji
  "The sidebar emoji of an entry of the custom emojis."
  [{:keys [short_names keywords imageUrl]}]
  (let [id (first short_names)]
    {:id id
     :label (label id)
     :keywords (into (vec short_names) keywords)
     :uri imageUrl
     :sticker? true}))

(def custom-groups
  "The custom emojis as `{:label :emojis}`, the seasonal ones last."
  (mapv (fn [[group-label emojis]]
          {:label group-label
           :emojis (mapv custom-emoji emojis)})
        custom-emojis/custom-emoji-groups))

(def custom
  (into [] (mapcat :emojis) custom-groups))

(defn- standard-emoji
  "The sidebar emoji of the emoji-mart `id`, nil without a Noto SVG.
   Emojis with skin tones use the neutral yellow one, like the former
   Picker with `:skin 1`."
  [id]
  (when-let [^js emoji (aget (.-emojis emojimart/emojiIndex) id)]
    (when-let [^js variant (if (.-unified emoji)
                             emoji
                             (aget emoji "1"))]
      (let [unified (.-unified variant)]
        (when (contains? noto-codes (noto-code unified))
          {:id id
           :label (label id)
           :uri (noto-uri unified)})))))

(def category-labels
  {"custom" "Storrito stickers"
   "people" "Smileys & people"
   "nature" "Animals & nature"
   "foods" "Food & drink"
   "activity" "Activity"
   "places" "Travel & places"
   "objects" "Objects"
   "symbols" "Symbols"})

(def categories
  "`{:id :label :emojis}` in the order of the former Picker. The custom
   category also has its `:groups`."
  (into [{:id "custom"
          :label (category-labels "custom")
          :emojis custom
          :groups custom-groups}]
        (keep (fn [^js category]
                (let [id (.-id category)]
                  (when (contains? category-labels id)
                    {:id id
                     :label (category-labels id)
                     :emojis (into [] (keep standard-emoji) (.-emojis category))}))))
        (.-categories emoji-data)))

(def ^:private standard-by-id
  (into {}
        (comp (remove (comp #{"custom"} :id))
              (mapcat :emojis)
              (map (juxt :id identity)))
        categories))

(defn category
  "The category with the `id`, or the first one."
  [id]
  (or (some (fn [category]
              (when (= id (:id category))
                category))
            categories)
      (first categories)))

(defn memoize-last
  "Like `memoize`, but only remembers the result of the last arguments.
   editor2 renders on every store change (60 times per second during
   playback), while the arguments of the Emoji sidebar rarely change."
  [f]
  (let [cache (atom nil)]
    (fn [& args]
      (let [[cached-args result] @cache]
        (if (= args cached-args)
          result
          (let [result (apply f args)]
            (reset! cache [args result])
            result))))))

(defn normalize-query
  "The trimmed, lower-case `query`, nil when blank."
  [query]
  (let [q (str/lower-case (str/trim (str query)))]
    (when (seq q)
      q)))

(defn keyword-match?
  "True if each word of the query `q` starts a word of the `keywords`,
   like the emoji-mart search: \"love\" finds \"love\" but not
   \"pullover\", and \"black fri\" finds \"black friday\"."
  [keywords q]
  (let [words (mapcat #(str/split (str/lower-case %) #"[\s_]+") keywords)]
    (every? (fn [q-word]
              (some #(str/starts-with? % q-word) words))
            (str/split q #"\s+"))))

(defn- search*
  [query]
  (when-let [q (normalize-query query)]
    (into (filterv (fn [{:keys [keywords]}]
                     (keyword-match? keywords q))
                   custom)
          (keep (fn [^js emoji]
                  (get standard-by-id (.-id emoji))))
          (or (.search emojimart/emojiIndex q)
              #js []))))

(def search
  "The emojis matching `query`: the custom ones by the word starts of
   their short names and keywords, the others via the emoji-mart index.
   Nil for a blank query."
  (memoize-last search*))

(comment
  (count noto-codes)

  (noto-uri "2764-fe0f")

  (map (juxt :id :label (comp count :emojis)) categories)

  (search "heart")

  (search "storrito")
  )
